<template>
  <TransitionFade>
    <div class="fixed inset-0 z-[100]">
      <div class="fixed inset-0 bg-black/75" @click="$emit('close')" aria-hidden="true"/>
      <div class="fixed top-0 right-0 bottom-0 w-full max-w-2xl rounded-l-md bg-white p-6 transition-all"
           :class="!showSlideOut && 'translate-x-36'">
        <XMarkIcon class="fixed top-7 right-6 h-6 w-6 cursor-pointer text-slate-400 transition-all hover:text-slate-600"
                   @click="$emit('close')"/>
        <div v-if="loading" class="flex h-full w-full items-center justify-center">
          <AnimationSpinningLoader color="#fff"/>
        </div>
        <div v-else-if="error" class="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-center">
          <ExclamationTriangleIcon class="h-6 w-6 text-red-500"/>
          <p>This interview was not found, or you do not have permission to view it.</p>
        </div>
        <template v-else>
          <p class="text-lg font-bold">{{ interview.title }}</p>
          <div class="flex items-center gap-2">
            <UserIcon class="h-4 w-4 text-gray-500"/>
            {{ interview.company.name }}
          </div>
          <hr class="mt-6 bg-slate-100"/>
          <InterviewSummary @reschedule="handleReschedule" @cancel="handleCancel" :interview="interview" show-team-members/>
        </template>
      </div>
    </div>
  </TransitionFade>
</template>

<script setup>
import AnimationSpinningLoader from "./AnimationSpinningLoader.vue";
import TransitionFade from "./TransitionFade.vue";
import InterviewSummary from "./InterviewSummary.vue";
import CandidateInterviewScheduleApi from "../api/CandidateInterviewScheduleApi.js";
import {onBeforeMount, onBeforeUnmount, onMounted, ref} from "vue";
import {UserIcon, XMarkIcon, ExclamationTriangleIcon} from "@heroicons/vue/24/outline";
import {objectToCamelCase} from "../helpers/CaseTransformer.js";
import {useRouter} from "vue-router";

const emits = defineEmits(["close", "interview-cancelled"]);
const props = defineProps({
  interviewId: Number,
});

const interview = ref(null);
const loading = ref(true);
const error = ref(false);
const rescheduling = ref(false);

const router = useRouter();

const getInterview = async () => {
  loading.value = true;
  error.value = false;
  await router.push({query: {interview: props.interviewId}});

  new CandidateInterviewScheduleApi()
    .showById(props.interviewId)
    .then(response => interview.value = objectToCamelCase(response.data))
    .catch(() => error.value = true)
    .finally(() => loading.value = false);
};

const handleReschedule = () => {
  rescheduling.value = true;
  router.push({
    path: `/schedule-interview/${interview.value.uuid}`,
    query: {reschedule: true},
  });
};

const handleCancel = async (reason) => {
  const uuid = interview.value.uuid;
  const id = interview.value.id;

  new CandidateInterviewScheduleApi()
    .destroy(uuid, {cancel_reason: reason})
    .then(() => {
      emits("interview-cancelled", id);
      toast.success("Interview cancelled successfully");
      emits("close");
    });
};

const showSlideOut = ref(false);
onBeforeMount(() => getInterview());
onMounted(() => setTimeout(() => showSlideOut.value = true, 50));

onBeforeUnmount(() => {
  showSlideOut.value = false;
  if (!rescheduling.value) router.push({query: {interview: null}});
});
</script>
